import { useQuery } from '@apollo/client';

import COURSE_REVIEW_QUERY from 'bundles/unified-description-page-common/queries/AllCourseReviews';
import type {
  AllCourseReviews,
  AllCourseReviewsVariables,
  AllCourseReviews_ProductReviewsV1Resource_reviews_elements,
} from 'bundles/unified-description-page-common/queries/__generated__/AllCourseReviews';

// The generated type is stale -- ProductReviewsV1's `AllCourseReviews.ts`/ReviewFragment.ts codegen
// output dates back to a 2019 schema shape (`users { publicDemographics { fullName } }`) and was
// never regenerated after the backend replaced that with a flat `fullName` scalar, because this
// query still lives in a hand-written .ts file rather than a `.graphql` file the codegen glob
// picks up. The real response has `fullName` (and no `users` field at all -- see the introspected
// schema), so it's widened in here rather than editing the generated file. `bundles/reviews`'
// `ReviewWithAuthor`/`getReviewAuthorName` independently work around the same staleness.
export type CourseReviewElement = AllCourseReviews_ProductReviewsV1Resource_reviews_elements & {
  fullName?: string | null;
};

type Options = {
  // Already in the "COURSE~<id>" stringkey shape ProductReviewsV1Resource.byProduct expects --
  // callers own that formatting, since only some of them (e.g. a raw course id from a filter
  // dropdown) need to add the prefix themselves.
  courseId?: string;
  limit: number;
  start: string;
  ratingValues?: number[];
  // Only ever sent to the query when explicitly `true` -- `false`/`undefined` both mean "don't
  // filter by completion" (sending `productCompleted: false` would instead mean "non-completers
  // only", which isn't what an unset/"all learners" filter means).
  productCompleted?: boolean;
  sortByHelpfulVotes: boolean;
  hasData?: boolean;
};

/**
 * Shared data fetch for a single course's reviews, reused by the standalone reviews page's
 * ReviewsTable (full pagination/filters) and the XDP specialization module's course-filtered top
 * reviews (fixed top-N, most-helpful-first). Returns raw, unmapped review elements -- each caller
 * derives author name, comment rendering, etc. the way its own UI needs.
 */
export const useCourseReviews = ({
  courseId,
  limit,
  start,
  ratingValues,
  productCompleted,
  sortByHelpfulVotes,
}: Options) => {
  const variables: AllCourseReviewsVariables = {
    courseId: courseId ?? '',
    limit,
    start,
    ratingValues,
    sortByHelpfulVotes,
  };
  if (productCompleted === true) {
    variables.productCompleted = true;
  }

  const { data, loading, error } = useQuery<AllCourseReviews, AllCourseReviewsVariables>(COURSE_REVIEW_QUERY, {
    variables,
    skip: !courseId,
    notifyOnNetworkStatusChange: true,
  });

  const reviews: CourseReviewElement[] = (data?.ProductReviewsV1Resource?.reviews?.elements ?? []).filter(
    (review): review is CourseReviewElement => review != null
  );
  const total = Number(data?.ProductReviewsV1Resource?.reviews?.paging?.total ?? 0);
  // True once a response (even an empty one) has actually arrived -- distinct from `total === 0`,
  // which is also true before the first response resolves. Lets callers tell "still loading" apart
  // from "loaded, and there's nothing there".
  const hasData = data?.ProductReviewsV1Resource?.reviews != null;
  // Convenience for callers that only care whether there's anything to show at all (e.g. gating a
  // nav link) -- false both while still loading and once loaded with zero results.
  const hasReviews = reviews.length > 0;

  return { reviews, total, loading, error, hasData, hasReviews };
};

export default useCourseReviews;
